import ls from 'localstorage-slim';
import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { cancelLeaseApplication } from '../../../api/instant-lease-api';
import {
  setIsAnimated,
  setNotification,
  setSubheaderVisibility,
} from '../../../redux/cache-slice';
import { fetchLeaseApp } from '../../../redux/leaseApp/lease-app-slice';
import { useAppDispatch } from '../../../redux/store';
import { RootState } from '../../../redux/types';
import { Status, Type } from '../../../types/instant-lease-api';
import {
  formatGermanPrice,
  getIsAllowedToBeCanceled,
  getText,
} from '../../../utils/getter';
import { ReactComponent as ArrowRightSm } from '../../b2b/components/icons/chevron-right-sm.svg';
import { ReactComponent as ContractCancel20 } from '../../b2b/components/icons/contract_delete_20.svg';
import Modal from '../../b2b/components/modal/modal';
import ButtonGroup from '../../b2b/molecules/button-group/button-group';
import CarDetails from '../car-details/car-details';
import { ReactComponent as CarButton } from '../icons/car-button.svg';
import { ReactComponent as ChevronDown } from '../icons/chevron-down-icon.svg';
import { ReactComponent as ChevronUp } from '../icons/chevron-up-icon.svg';
import PricingDetails from '../pricing-details/pricing-details';
import './sub-header.css';

const SubHeader: React.FC<{
  forceEnable?: boolean;
  showCancelButton?: boolean;
  bounceArrow?: boolean;
}> = ({ forceEnable, showCancelButton, bounceArrow }) => {
  // Lease Applications Store
  const { carData, activeApplication, leaseApplications } = useSelector(
    (state: RootState) => state.leaseApp,
  );
  // Lease Application Storage
  const { accessToken } = useSelector((state: RootState) => state.auth);

  // sequence Storage
  const { device } = useSelector((state: RootState) => state.sequence);

  // History hook
  const navigate = useNavigate();

  // pathname
  const { pathname } = useLocation();

  // Cache Store
  const { isSubheaderVisible, isAnimated } = useSelector(
    (state: RootState) => state.cache,
  );

  // Scroll effect controller for header
  const [scrolledHeader, setScrolledHeader] = useState<boolean>(true);

  // Dispatch hook
  const dispatch = useAppDispatch();

  // Subheader visibility
  const [priceChangeModalVisibility, setPriceChangeModalVisibility] =
    useState<boolean>(false);

  const subHeaderRef = useRef<HTMLDivElement>(null);

  // Variables
  const isCommercial = activeApplication?.type === Type.B2C;

  const vehicleData = activeApplication?.offer?.vehicle_data;

  const leasingOptions = activeApplication?.offer?.leasing_options;

  const vehicleRate = isCommercial
    ? leasingOptions?.totals?.rate?.gross
    : leasingOptions?.totals?.rate?.net;

  // Subheader Control
  const isSubheaderEnabled = !(
    (!carData && !vehicleData) ||
    activeApplication?.state === Status.CANCELLED
  );

  const toggleSubheader = () => {
    const subheaderElement = subHeaderRef.current;

    if (isSubheaderVisible) {
      if (subheaderElement) {
        // If the panel was left at `max-height: none`, restore a concrete value
        // before animating back to 0.
        if (subheaderElement.style.maxHeight === 'none') {
          subheaderElement.style.maxHeight = `${subheaderElement.scrollHeight}px`;
        }

        subheaderElement.style.maxHeight = `${subheaderElement.scrollHeight}px`;
        requestAnimationFrame(() => {
          subheaderElement.style.maxHeight = '0';
        });
      }

      setTimeout(() => {
        dispatch(setSubheaderVisibility(false));
      }, 500);
    } else {
      dispatch(setSubheaderVisibility(true));

      if (subheaderElement) {
        subheaderElement.style.maxHeight = '0';
        requestAnimationFrame(() => {
          subheaderElement.style.maxHeight = `${subheaderElement.scrollHeight}px`;
        });

        // Prevent clipping of bottom content (including close toggle) once open.
        setTimeout(() => {
          if (
            subHeaderRef.current &&
            subHeaderRef.current.style.maxHeight !== '0px'
          ) {
            subHeaderRef.current.style.maxHeight = 'none';
          }
        }, 10);
      }
    }
  };

  // handle price change modal
  useEffect(() => {
    // PCMS stand for price change modal status
    const pcms = ls.get('pcms');
    // If the price change again, the user will see the modal again
    const rateHasChanged = vehicleRate !== pcms;

    if (
      activeApplication &&
      carData?.rate !== undefined &&
      vehicleRate !== undefined &&
      carData?.rate !== vehicleRate &&
      carData?.carFullName === vehicleData?.full_name &&
      rateHasChanged &&
      isSubheaderEnabled
    ) {
      setPriceChangeModalVisibility(true);
    } else {
      setPriceChangeModalVisibility(false);
    }
  }, [
    carData?.rate,
    vehicleRate,
    activeApplication?.uuid,
    isSubheaderEnabled,
    leaseApplications.length,
    activeApplication,
    carData?.carFullName,
    vehicleData?.full_name,
  ]);

  useEffect(() => {
    window.onscroll = () => {
      const scrollMargin = document.documentElement.scrollTop;
      if (scrollMargin > 10) {
        setScrolledHeader(true);
      } else {
        setScrolledHeader(true);
      }
    };
  }, []);

  useEffect(() => {
    const bootstrapPage = ls.get('bootstrapPage');

    if (pathname.includes('/login') && bootstrapPage) {
      //  Open immediately (no animation)
      dispatch(setIsAnimated(false));
      dispatch(setSubheaderVisibility(true));

      const el = subHeaderRef.current;
      if (!el) return;

      // Helper to close with smooth animation
      const closeSmoothly = () => {
        // Force the element to have its full height
        el.style.maxHeight = `${el.scrollHeight}px`;

        // Wait one frame so the browser registers the maxHeight
        requestAnimationFrame(() => {
          dispatch(setIsAnimated(true));
          window.scrollTo({ top: 0, behavior: 'smooth' });

          // Trigger the close animation
          el.style.transition =
            'max-height 0.5s ease-in-out, padding 0.5s ease-in-out';
          el.style.maxHeight = '0';
        });
      };

      // If images exist, wait for them to load
      const images = el.querySelectorAll('img');
      if (images.length === 0) {
        requestAnimationFrame(closeSmoothly);
      } else {
        let loadedCount = 0;
        images.forEach((img) => {
          if (img.complete) loadedCount++;
          else
            img.onload = () => {
              loadedCount++;
              if (loadedCount === images.length) closeSmoothly();
            };
        });
        if (loadedCount === images.length) requestAnimationFrame(closeSmoothly);
      }
    }

    let innerTimeoutId: ReturnType<typeof setTimeout> | undefined;
    const timeoutId = setTimeout(
      () => {
        if (pathname.includes('/login')) {
          dispatch(setSubheaderVisibility(true));
          dispatch(setIsAnimated(false));

          innerTimeoutId = setTimeout(() => {
            dispatch(setSubheaderVisibility(false));
            window.scrollTo({ top: 0, behavior: 'smooth' });
          }, 400);
        }
      },
      isAnimated ? 0 : 3001,
    );

    return () => {
      clearTimeout(timeoutId);
      clearTimeout(innerTimeoutId);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname, dispatch]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      if (isSubheaderVisible) {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
      if (!isSubheaderVisible) {
        const activeSequenceList = document.querySelectorAll(
          '.sequence-container.active',
        );

        if (activeSequenceList.length > 0) {
          const activeSequence = activeSequenceList[0];
          activeSequence.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
            inline: 'center',
          });
        } else {
          window.scrollTo({ top: 0, behavior: 'smooth' });
        }
      }
    }, 100);
    return () => clearTimeout(timeout);
  }, [isSubheaderVisible]);

  // Format price with german price format
  const { format } = formatGermanPrice();

  // Handle cancel leasing application
  const cancelLeaseApp = () => {
    if (getIsAllowedToBeCanceled(activeApplication?.state)) {
      setPriceChangeModalVisibility(false);
      cancelLeaseApplication(accessToken, activeApplication?.uuid as string)
        .then(() => {
          dispatch(
            fetchLeaseApp({
              accessToken,
              leaseAppId: activeApplication?.uuid as string,
            }),
          ).then(() => {
            ls.set('pcms', vehicleRate);
            navigate('/');
          });
        })
        .catch(() => {
          setPriceChangeModalVisibility(true);
          dispatch(
            setNotification({
              notificationVisibility: true,
              notificationHasError: true,
              notificationTitle: 'Das tut uns leid',
              notificationBody: getText('error_unable_to_process_data'),
            }),
          );
        });
    }
  };
  // If subheader disabled or not forced
  if (!forceEnable && !isSubheaderEnabled) {
    return <div />;
  }

  return (
    <>
      <div className={`subheader`}>
        <PricingDetails visibility={isSubheaderVisible} />
        {!isSubheaderVisible && (
          <div className='subheader-toggle open'>
            <div
              className='focus-style'
              role='button'
              tabIndex={0}
              aria-expanded='false'
              aria-label='Mein Fahrzeug einblenden'
              onClick={toggleSubheader}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  toggleSubheader();
                }
              }}
            >
              <CarButton className='car-button' />
              <span className='content-normal'>Mein Fahrzeug</span>
              <ChevronDown
                className={`mobile-hidden ${bounceArrow ? 'bounce' : ''}`}
              />
            </div>
          </div>
        )}
      </div>
      <div
        ref={subHeaderRef}
        className={`subheader-body ${scrolledHeader ? 'active' : ''} ${
          isSubheaderVisible ? 'visible' : ''
        }`}
      >
        <div className={`${isSubheaderVisible ? 'visible' : 'hidden'}`}>
          <CarDetails showCancelButton={showCancelButton} />
          {isSubheaderVisible && (
            <div className='subheader-toggle close'>
              <div
                className='focus-style'
                role='button'
                tabIndex={0}
                aria-expanded='true'
                aria-label='Mein Fahrzeug ausblenden'
                onClick={() => {
                  window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
                  toggleSubheader();
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    toggleSubheader();
                  } else if (e.key === 'Tab' && !e.shiftKey) {
                    e.preventDefault();
                    toggleSubheader();
                  }
                }}
              >
                <CarButton className='car-button' />
                <span className='content-normal'>Mein Fahrzeug</span>
                <ChevronUp
                  className={`mobile-hidden ${bounceArrow ? 'bounce' : ''}`}
                />
              </div>
            </div>
          )}
        </div>
      </div>
      <Modal
        type={device === 'mobile' ? 'top' : 'middle'}
        direction='down'
        isOpen={priceChangeModalVisibility}
        closeButtonVisibility={false}
      >
        <div className='flex flex-row' style={{ rowGap: '30px' }}>
          <h1 className='title'>Änderung der Leasingrate</h1>
          <div className='flex'>
            <div>
              <ArrowRightSm />
            </div>
            <p
              className='left content-normal'
              style={{
                paddingTop: device === 'mobile' ? '4px' : '0px',
              }}
            >
              Der Hersteller hat eine Preisänderung vorgenommen. Daher wurde
              dein Angebot neu berechnet und die auf der
              Fahrzeugkonfigurationsseite angegebene Leasingrate von{' '}
              {format(carData?.rate || 0)} wurde auf {format(vehicleRate || 0)}{' '}
              aktualisiert. Du kannst deinen Leasingantrag mit der
              aktualisierten Rate fortsetzen oder den Leasingantrag stornieren.
            </p>
          </div>
          <ButtonGroup
            type='default'
            style={{ width: 'auto' }}
            className='top-24'
            buttonOneProps={{
              theme: 'primary',
              className: 'content-normal button',
              children: 'Mit dem aktualisierten Angebot fortfahren',
              type: 'button',
              onClick: () => {
                ls.set('pcms', vehicleRate);
                setPriceChangeModalVisibility(false);
              },
            }}
            buttonTwoProps={{
              theme: 'secondary',
              className: 'button--secondary-danger',
              children: (
                <span className='cancel-button-secondary'>
                  <ContractCancel20 />
                  Leasingantrag stornieren
                </span>
              ),
              type: 'button',
              onClick: () => {
                cancelLeaseApp();
              },
            }}
          />
        </div>
      </Modal>
    </>
  );
};

export default SubHeader;
